/**
 * Installing the PWA (2026-10-11).
 *
 * Two very different roads to the same place. Chrome and Edge (Android and
 * desktop) fire `beforeinstallprompt`, which we hold on to and replay behind
 * our own button. iOS fires nothing: the only way in is Share → Add to Home
 * Screen, and on iPhone that is also the only place web push works at all, so
 * an iPhone in a Safari tab has to be shown the steps.
 */

import { useSyncExternalStore } from 'react';
import { track } from '../onboarding/track';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

/** Opened from the Home Screen / as an installed app, not in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone and iPad, including iPadOS that reports itself as a Mac. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Call once at start-up: the event fires early and only once per page. */
export function listenForInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    track({ event: 'app_installed' });
    changed();
  });
}

/** Whether the browser will show its own install dialog behind our button. */
export function useCanPromptInstall(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => deferred != null && !isStandalone(),
    () => false,
  );
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  changed();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}
